import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import {
  decodeUrlOrAddress,
  encodeLud06,
  shortenKey,
} from "@/Helpers/Encryptions";
import QRCode from "react-qr-code";
import { relaysOnPlatform } from "@/Content/Relays";
import { getZapEventRequest } from "@/Helpers/NostrPublisher";
import LoadingDots from "@/Components/LoadingDots";
import { webln } from "@getalby/sdk";
import { decode } from "light-bolt11-decoder";
import { getWallets, updateWallets } from "@/Helpers/ClientHelpers";
import { useDispatch, useSelector } from "react-redux";
import { setToast } from "@/Store/Slides/Publishers";
import { useTranslation } from "react-i18next";
import useUserProfile from "@/Hooks/useUsersProfile";
import { nip19 } from "nostr-tools";
import Lottie from "lottie-react";
import PagePlaceholder from "@/Components/PagePlaceholder";
import Link from "next/link";
import { saveUsers } from "@/Helpers/DB";
import successJSON from "@/JSONs/success.json";
import useCashu from "@/Hooks/useCachu";
import { swapTokensInvoiceFromMint } from "@/Helpers/CashuHelpers";
import Icon from "@/Components/Icon";

export default function PaymentGateway({
  recipientAddr,
  paymentAmount,
  recipientPubkey,
  nostrEventIDEncode,
  setReceivedEvent = () => null,
  setConfirmPayment = () => null,
  exit,
}) {
  const { t } = useTranslation();
  const [callback, setCallback] = useState(false);
  const [lnbcAmount, setLnbcAmount] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { userProfile } = useUserProfile(recipientPubkey);
  const wallets = getWallets();

  useEffect(() => {
    const fetchData = async () => {
      if (callback) return;
      try {
        if (!recipientAddr && wallets.length === 0) {
          setConfirmPayment({ status: false, preImage: "" });
          setIsLoading(false);
          return;
        }
        if (recipientAddr.startsWith("lnbc") && recipientAddr.length > 24) {
          try {
            let decoded = decode(recipientAddr);
            let lnbc = decoded.sections.find(
              (section) => section.name === "amount",
            );
            setLnbcAmount(parseInt(lnbc.value) / 1000);
            setIsLoading(false);
            return;
          } catch (err) {
            console.log(err);
            setConfirmPayment({ status: false, preImage: "" });
            setIsLoading(false);
            return;
          }
        }

        let url = decodeUrlOrAddress(recipientAddr);
        if (!url) return;
        const data = await axios.get(url);

        setCallback(data.data.callback);
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setConfirmPayment({ status: false, preImage: "" });
        setIsLoading(false);
      }
    };
    if (!callback) fetchData();
  }, [recipientAddr]);

  if (isLoading)
    return (
      <div className="fixed-container fx-centered" style={{ zIndex: 2000000 }}>
        <LoadingDots />
      </div>
    );
  if (wallets.length === 0)
    return (
      <div
        className="fixed-container fx-centered box-pad-h"
        style={{ zIndex: 2000000 }}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="sc-s bg-sp box-pad-h box-pad-v fx-centered fx-col slide-up"
          style={{
            width: "min(100%, 400px)",
            position: "relative",
            overflow: "visible",
            padding: "3rem 1rem",
          }}
        >
          <div className="close" onClick={exit}>
            <div></div>
          </div>
          <PagePlaceholder page={"nostr-add-wallet"} />
          <Link href={"/lightning-wallet"} target="_blank">
            <button className="btn btn-normal">{t("A8fEwNq")}</button>
          </Link>
        </div>
      </div>
    );
  if (
    !recipientAddr ||
    (!callback && !recipientAddr.startsWith("lnbc")) ||
    (!lnbcAmount && recipientAddr.startsWith("lnbc"))
  )
    return (
      <div
        className="fixed-container fx-centered box-pad-h"
        style={{ zIndex: 2000000 }}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="sc-s bg-sp box-pad-h box-pad-v fx-centered fx-col slide-up"
          style={{
            width: "min(100%, 400px)",
            position: "relative",
            overflow: "visible",
          }}
        >
          <div className="close" onClick={exit}>
            <div></div>
          </div>
          <Icon name="crossmark-tt" size={50} isColored/>
          <h4>{t("AI8bhpw")}</h4>
          <p className="box-pad-h gray-c p-centered">{t("ACOXf0z")}</p>
        </div>
      </div>
    );
  return (
    <Cashier
      recipientAddr={recipientAddr}
      recipientPubkey={recipientPubkey}
      callback={callback}
      recipientInfo={userProfile}
      nostrEventIDEncode={nostrEventIDEncode}
      paymentAmount={lnbcAmount || paymentAmount}
      isLNBC={lnbcAmount}
      exit={exit}
      setReceivedEvent={setReceivedEvent}
      setConfirmPayment={setConfirmPayment}
    />
  );
}

const Cashier = ({
  recipientAddr,
  recipientPubkey,
  callback,
  nostrEventIDEncode,
  paymentAmount,
  isLNBC = false,
  exit,
  setReceivedEvent,
  setConfirmPayment,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userKeys = useSelector((state) => state.userKeys);
  const userMetadata = useSelector((state) => state.userMetadata);
  const [amount, setAmount] = useState(paymentAmount || 21);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [invoice, setInvoice] = useState("");
  const [wallets, setWallets] = useState(getWallets());
  const [selectedWallet, setSelectedWallet] = useState(
    wallets.find((wallet) => wallet.active),
  );
  const [confirmation, setConfirmation] = useState("initiated");
  const { cashuTokens, cashuWalletMints } = useCashu();
  const [onlyInvoice, setOnlyInvoice] = useState(false);
  const [showWalletsList, setShowWalletList] = useState(false);
  const walletListRef = useRef(null);

  useEffect(() => {
    let handleOffClick = (e) => {
      if (walletListRef.current && !walletListRef.current.contains(e.target)) {
        setShowWalletList(false);
      }
    };

    document.addEventListener("mousedown", handleOffClick);
    return () => {
      document.removeEventListener("mousedown", handleOffClick);
    };
  }, [walletListRef]);

  const onConfirmation = async (generateOnlyInvoice) => {
    try {
      if (!userKeys || !amount) {
        dispatch(
          setToast({
            type: 2,
            desc: t("AbnA22A"),
          }),
        );
        return;
      }
      setIsLoading(true);
      let lnbcInvoice = isLNBC ? recipientAddr : "";
      let eventCreatedAt = Math.floor(Date.now() / 1000);
      let eventToPublish = null;
      if (!isLNBC) {
        let sats = amount * 1000;
        let tags = [
          ["relays", ...relaysOnPlatform],
          ["amount", sats.toString()],
          ["lnurl", recipientAddr],
          ["p", recipientPubkey],
        ];
        let extraTags = nostrEventIDEncode
          ? getNostrEventInfo(nostrEventIDEncode)
          : [];
        tags = [...tags, ...extraTags];
        const event = recipientPubkey
          ? await getZapEventRequest(userKeys, message, tags, eventCreatedAt)
          : false;

        eventToPublish = event;
        let tempRecipientLNURL = recipientAddr.includes("@")
          ? encodeLud06(decodeUrlOrAddress(recipientAddr))
          : recipientAddr;
        try {
          const res = await axios(
            `${callback}${callback.includes("?") ? "&" : "?"}amount=${sats}${
              event ? `&nostr=${event}` : ""
            }&lnurl=${tempRecipientLNURL}`,
          );
          if (res.data.status === "ERROR") {
            setIsLoading(false);
            setConfirmation("failed");
            dispatch(
              setToast({
                type: 2,
                desc: t("AZ43zpG"),
              }),
            );
            return;
          }
          lnbcInvoice = res.data.pr;
        } catch (err) {
          setConfirmation("failed");
          setIsLoading(false);
          dispatch(
            setToast({
              type: 2,
              desc: t("AgCBh6S"),
            }),
          );
          return;
        }
      }
      setInvoice(lnbcInvoice);
      setConfirmation("in_progress");

      if (generateOnlyInvoice) {
        setIsLoading(false);
        setOnlyInvoice(true);
        return;
      }

      let res = await sendPayment(lnbcInvoice);
      setConfirmPayment(res);
      if (eventToPublish) {
        setReceivedEvent({
          kinds: [9735],
          "#p": [recipientPubkey],
          since: eventCreatedAt - 1,
        });
      }
      if (res.status) {
        setConfirmation("confirmed");
      } else {
        setConfirmation("failed");
      }
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setConfirmation("failed");
      setIsLoading(false);
    }
  };

  const getNostrEventInfo = (id) => {
    let tags = [];
    try {
      if (id.startsWith("note1")) {
        let decode = nip19.decode(id);
        return [["e", decode.data]];
      }
      if (id.startsWith("nevent1")) {
        let decode = nip19.decode(id);
        return [["e", decode.data.id]];
      }
      if (id.startsWith("naddr")) {
        let decode = nip19.decode(id);
        return [
          [
            "a",
            `${decode.data.kind}:${decode.data.pubkey}:${decode.data.identifier}`,
          ],
        ];
      }

      return [];
    } catch (err) {
      return tags;
    }
  };

  const sendPayment = async (addr) => {
    if (selectedWallet.kind === 1) {
      let res = await sendWithWebLN(addr);
      return res;
    }
    if (selectedWallet.kind === 2) {
      let checkTokens = await checkAlbyToken(wallets, selectedWallet);
      setWallets(checkTokens.wallets);
      let res = await sendWithAlby(
        addr,
        checkTokens.activeWallet.data.access_token,
      );
      return res;
    }
    if (selectedWallet.kind === 3) {
      let res = await sendWithNWC(addr);
      return res;
    }
    if (selectedWallet.kind === -1) {
      let res = await swapTokensInvoiceFromMint({
        mintFrom: selectedWallet.url,
        invoice: addr,
        cashuTokens,
      });
      return res;
    }
  };

  const sendWithWebLN = async (addr_) => {
    try {
      await window.webln?.enable();
      let res = await window.webln.sendPayment(addr_);
      return {
        status: res.preimage ? true : false,
        preImage: res.preimage,
      };
    } catch (err) {
      setIsLoading(false);
      if (err.includes("User rejected")) return;
      dispatch(
        setToast({
          type: 2,
          desc: t("Acr4Slu"),
        }),
      );
      return {
        status: false,
        preImage: "",
      };
    }
  };

  const sendWithNWC = async (addr_) => {
    try {
      const nwc = new webln.NWC({ nostrWalletConnectUrl: selectedWallet.data });
      await nwc.enable();
      const res = await nwc.sendPayment(addr_);
      nwc.close();
      return {
        status: res.preimage ? true : false,
        preImage: res.preimage,
      };
    } catch (err) {
      console.log(err);
      setIsLoading(false);
      dispatch(
        setToast({
          type: 2,
          desc: t("Acr4Slu"),
        }),
      );
      return {
        status: false,
        preImage: "",
      };
    }
  };

  const sendWithAlby = async (addr_, code) => {
    try {
      const res = await axios.post(
        "https://api.getalby.com/payments/bolt11",
        { invoice: addr_ },
        {
          headers: {
            Authorization: `Bearer ${code}`,
          },
        },
      );
      return {
        status: res.data.preimage ? true : false,
        preImage: res.data.preimage,
      };
    } catch (err) {
      setIsLoading(false);
      console.log(err);
      return {
        status: false,
        preImage: "",
      };
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    dispatch(
      setToast({
        type: 1,
        desc: `${t("AS0m8W5")} 👏`,
      }),
    );
  };

  const handleSelectLightningWallet = (walletID) => {
    let index = wallets.findIndex((wallet) => wallet.id == walletID);

    let tempWallets = Array.from(wallets);
    tempWallets = tempWallets.map((wallet) => {
      let w = { ...wallet };
      w.active = false;
      return w;
    });
    tempWallets[index].active = true;
    setSelectedWallet(wallets[index]);
    setWallets(tempWallets);
    setShowWalletList(false);
  };

  const handleSelectCachuMints = (mint) => {
    setSelectedWallet({
      kind: -1,
      ...mint,
    });
    setShowWalletList(false);
  };

  return (
    <div
      className="fixed-container fx-centered box-pad-h"
      onClick={(e) => {
        e.stopPropagation();
        exit();
      }}
      style={{ zIndex: 2000000 }}
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="sc-s bg-sp box-pad-h box-pad-v slide-up"
        style={{
          width: "min(100%, 400px)",
          position: "relative",
          overflow: "visible",
        }}
      >
        {confirmation === "initiated" && (
          <div className="fx-centered fx-col fit-container fx-start-v">
            <div className="fit-container fx-centered fx-col">
              {recipientPubkey && (
                <div
                  className="fx-centered sc-s bg-sp"
                  style={{ padding: ".35rem .45rem", gap: "20px" }}
                >
                  <div
                    className="bg-img cover-bg pointer"
                    style={{
                      minHeight: "30px",
                      minWidth: "30px",
                      backgroundImage: `url(${userMetadata?.picture})`,
                      borderRadius: "50%",
                    }}
                  ></div>
                  <div style={{ position: "relative" }}>
                    <div className="arrows-animated">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <ReceiverInfo
                    pubkey={recipientPubkey}
                    recipientAddr={recipientAddr}
                    isLNBC={isLNBC}
                  />
                </div>
              )}
              <div className="fit-container fx-centered" style={{ gap: 0 }}>
                <div
                  style={{
                    position: "relative",
                    width: showWalletsList ? "350px" : "200px",
                    transition: "width .2s ease-in-out",
                  }}
                  ref={walletListRef}
                >
                  {selectedWallet && (
                    <div
                      className="box-pad-h-s sc-s bg-sp fx-scattered option pointer fit-container"
                      onClick={() => setShowWalletList(!showWalletsList)}
                      style={{ padding: ".25rem .5rem" }}
                    >
                      <div className="fx-centered">
                        {selectedWallet.kind === 1 && (
                          <div className="round-icon-small">
                            <Icon name="webln-logo" size={24} isColored/>
                          </div>
                        )}
                        {selectedWallet.kind === 2 && (
                          <div className="round-icon-small">
                            <Icon name="alby-logo" size={24} isColored/>
                          </div>
                        )}
                        {selectedWallet.kind === 3 && (
                          <div className="round-icon-small">
                            <Icon name="nwc-logo" size={24} isColored/>
                          </div>
                        )}
                        {selectedWallet.kind === -1 && (
                          <div className="round-icon-small">
                            <div
                              style={{
                                backgroundImage: `url(${selectedWallet.data.icon_url})`,
                                minWidth: "24px",
                                minHeight: "24px",
                                borderRadius: "50%",
                                backgroundColor: "var(--pale-gray)",
                              }}
                              className="bg-img cover-bg"
                            ></div>
                          </div>
                        )}
                        <div>
                          <p className="gray-c p-medium">{t("A7r9XS1")}</p>
                          <p className="p-one-line">
                            {selectedWallet.entitle ||
                              selectedWallet?.data?.name}
                          </p>
                        </div>
                      </div>
                      <div className="box-pad-h-s"></div>
                      <Icon name="arrow" />
                    </div>
                  )}
                  {showWalletsList && (
                    <div
                      className="fx-centered fx-col sc-s-18 bg-sp box-pad-h-s box-pad-v-s fx-start-v fx-start-h fit-container"
                      style={{
                        // width: "400px",
                        backgroundColor: "var(--c1-side)",
                        position: "absolute",
                        right: "0",
                        top: "calc(100% + 5px)",
                        rowGap: 0,
                        overflow: "scroll",
                        maxHeight: "300px",
                        zIndex: 100,
                      }}
                    >
                      <p className="p-medium gray-c  box-pad-v-s">
                        {t("AOSWanf")}
                      </p>
                      {wallets.map((wallet) => {
                        return (
                          <div
                            key={wallet.id}
                            className={`option-no-scale fit-container fx-scattered pointer box-pad-h-s box-pad-v-s ${selectedWallet.kind !== -1 && wallet.active ? "sc-s-18" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectLightningWallet(wallet.id);
                            }}
                            style={{
                              border: "none",
                              // minWidth: "max-content",
                              overflow: "visible",
                            }}
                          >
                            <div className="fx-centered">
                              {wallet.kind === 1 && (
                                <div
                                  className="round-icon"
                                  style={{
                                    minWidth: "32px",
                                    minHeight: "32px",
                                  }}
                                >
                                  <Icon name="webln-logo" size={24} isColored/>
                                </div>
                              )}
                              {wallet.kind === 2 && (
                                <div
                                  className="round-icon"
                                  style={{
                                    minWidth: "32px",
                                    minHeight: "32px",
                                  }}
                                >
                                  <Icon name="alby-logo" size={24} isColored/>
                                </div>
                              )}
                              {wallet.kind === 3 && (
                                <div
                                  className="round-icon"
                                  style={{
                                    minWidth: "32px",
                                    minHeight: "32px",
                                  }}
                                >
                                  <Icon name="nwc-logo" size={24} isColored/>
                                </div>
                              )}
                              <p className="p-one-line">{wallet.entitle}</p>
                            </div>
                          </div>
                        );
                      })}
                      {cashuWalletMints.length > 0 && (
                        <>
                          <p className="p-medium gray-c box-pad-v-s">
                            {t("AiYbIZT")}
                          </p>
                          {cashuWalletMints.map((mint) => {
                            return (
                              <div
                                className={`pointer fx-scattered fit-container box-pad-h-s box-pad-v-s option-no-scale ${selectedWallet.kind === -1 && mint.url === selectedWallet?.url ? "sc-s-18" : ""}`}
                                onClick={() => {
                                  handleSelectCachuMints(mint);
                                }}
                                style={{
                                  border: "none",
                                  overflow: "visible",
                                }}
                                key={mint.url}
                              >
                                <div className="fx-centered">
                                  <div
                                    style={{
                                      backgroundImage: `url(${mint.data.icon_url})`,
                                      minWidth: "32px",
                                      minHeight: "32px",
                                      borderRadius: "50%",
                                      backgroundColor: "var(--pale-gray)",
                                    }}
                                    className="bg-img cover-bg"
                                  ></div>
                                  <div>
                                    <p className="p-caps p-one-line">
                                      {mint.data.name}
                                    </p>
                                    <p className="gray-c p-medium p-one-line">
                                      {mint.url}
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="sticker sticker-green-side sticker-c1"
                                  style={{ minWidth: "max-content" }}
                                >
                                  Max {cashuTokens[mint.url]?.total || 0} sats
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {!isLNBC && !paymentAmount && (
                <>
                  <div className="fx-centered fx-col box-pad-v-m box-pad-h-s">
                    <div className="fx-centered fx-col">
                      <p className="gray-c p-big">{t("A82pzWN")}</p>
                      <input
                        type="number"
                        className="if p-bold if-no-border ifs-full p-centered"
                        placeholder={t("AcDgXKI")}
                        style={{
                          fontSize: `max(${
                            amount.toString().length > 5
                              ? `${80 - (amount.toString().length - 6) * 10}px`
                              : "80px"
                          },50px)`,
                          height: "80px",
                        }}
                        value={amount}
                        onChange={(e) => setAmount(parseInt(e.target.value))}
                        autoFocus
                      />
                      <p className="gray-c p-big">Sats</p>
                    </div>
                    <input
                      type="text"
                      className="if ifs-full if-no-border p-centered"
                      style={{
                        borderTop: "1px solid var(--pale-gray)",
                        borderBottom: "1px solid var(--pale-gray)",
                        borderRadius: "0",
                        height: "50px",
                      }}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t("Ark6BLW")}
                    />
                  </div>
                </>
              )}
              {(isLNBC ||
                (paymentAmount !== 0 && paymentAmount !== undefined)) && (
                <div className="fx-centered fx-col box-pad-v-m">
                  <div className="fx-centered fx-col">
                    <p className="gray-c p-big">{t("A82pzWN")}</p>

                    <h1 style={{ fontSize: "80px" }}>{amount}</h1>
                    <p className="gray-c p-big">Sats</p>
                  </div>
                </div>
              )}
            </div>
            <div className="fit-container fx-centered" style={{ gap: "16px" }}>
              <div
                className="fx-centered fx-col option pointer fx"
                style={{
                  height: "95px",
                  borderRadius: "24px",
                  border: "1px solid var(--red-main)",
                }}
                onClick={isLoading ? null : exit}
              >
                {isLoading ? (
                  <LoadingDots />
                ) : (
                  <>
                    <p className="red-c p-big" style={{ height: "20px" }}>
                      &#10005;
                    </p>
                    <p className="red-c">{t("AB4BSCe")}</p>
                  </>
                )}
              </div>
              <div
                className="fx-centered fx-col option pointer fx"
                style={{
                  height: "95px",
                  borderRadius: "24px",
                  border: "1px solid var(--pale-gray)",
                }}
                onClick={() => {
                  !isLoading && onConfirmation(true);
                }}
              >
                {isLoading ? (
                  <LoadingDots />
                ) : (
                  <>
                    <Icon name="qrcode" size={24} />
                    <p>{t("AvEHTiP")}</p>
                  </>
                )}
              </div>
              <div
                className="fx-centered fx-col option pointer fx"
                style={{
                  height: "95px",
                  borderRadius: "24px",
                  backgroundColor: "var(--c1)",
                }}
                onClick={() => (isLoading ? null : onConfirmation())}
              >
                {isLoading ? (
                  <LoadingDots />
                ) : (
                  <>
                    <p className="p-big" style={{ height: "20px" }}>
                      &#8593;
                    </p>
                    <p>{t("A14LwWS")}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {confirmation === "in_progress" && (
          <div className="fx-centered fx-col fit-container">
            <h4>{t("AvEHTiP")}</h4>
            <p className="gray-c box-pad-v-s box-pad-h p-centered">
              {t("ASopYJK")}
            </p>
            <div
              style={{ backgroundColor: "white" }}
              className="sc-s-18 box-pad-h-m box-pad-v-m fx-centered fit-container"
            >
              <QRCode
                // style={{ width: "100%", aspectRatio: "1/1" }}
                size={320}
                value={invoice}
              />
            </div>
            <div
              className="fx-scattered if pointer dashed-onH fit-container"
              style={{ borderStyle: "dashed" }}
              onClick={() => copyKey(invoice)}
            >
              <p>{shortenKey(invoice)}</p>
              <Icon name="copy" size={24} />
            </div>
            {!onlyInvoice && (
              <div className="fit-container fx-centered box-pad-v-s">
                <p className="gray-c p-medium">{t("A1ufjMM")}</p>
                <LoadingDots />
              </div>
            )}
            {onlyInvoice && (
              <div className="fit-container fx-centered">
                <button
                  className="btn btn-normal btn-full"
                  onClick={() => {
                    exit();
                  }}
                >
                  {t("AI67awJ")}
                </button>
              </div>
            )}
          </div>
        )}
        {confirmation === "confirmed" && (
          <div
            className="fx-centered fx-col fit-container"
            style={{ height: "25vh" }}
          >
            <div style={{ maxHeight: "70px", maxWidth: "70px" }}>
              <Lottie animationData={successJSON} loop={false} />
            </div>
            <h4 className="slide-down">{t("ACDUO1d")}</h4>
            <p className="gray-c box-pad-v-s slide-up">
              {t("ALEgwqA")} <span className="orange-c">{amount} sats</span>
            </p>
            <button className="btn btn-normal slide-up" onClick={exit}>
              {t("Acglhzb")}
            </button>
          </div>
        )}
        {confirmation === "failed" && (
          <div
            className="fx-centered fx-col fit-container"
            style={{ height: "16vh" }}
          >
            <Icon name="crossmark-tt" size={50} isColored/>
            <h4 className="slide-down box-pad-v-m">{t("AI8bhpw")}</h4>
            <button className="btn btn-normal slide-up" onClick={exit}>
              {t("Acglhzb")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ReceiverInfo = ({ pubkey, isLNBC, recipientAddr }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { isNip05Verified, userProfile } = useUserProfile(pubkey);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    if (!(userProfile.display_name && userProfile.picture)) saveUsers([pubkey]);
  }, []);

  useEffect(() => {
    const handleOffClick = (e) => {
      e.stopPropagation();
      if (ref.current && !ref.current.contains(e.target)) setShowInfo(false);
    };
    document.addEventListener("mousedown", handleOffClick);
    return () => {
      document.removeEventListener("mousedown", handleOffClick);
    };
  }, [ref]);
  return (
    <div style={{ position: "relative", zIndex: "100" }} ref={ref}>
      <div
        className="bg-img cover-bg pointer"
        style={{
          minHeight: "30px",
          minWidth: "30px",
          backgroundImage: `url(${userProfile?.picture})`,
          borderRadius: "50%",
        }}
        onClick={() => setShowInfo(!showInfo)}
      ></div>

      {showInfo && (
        <div
          className="sc-s bg-sp box-pad-h box-pad-v-s slide-left"
          style={{
            position: "absolute",
            top: "0",
            left: "calc(100% + 8px)",
            width: "max-content",
            maxWidth: "300px",
            height: "100%",
            zIndex: 101,
          }}
        >
          <div className="fx-centered fx-start-h">
            <p className="p-maj">{userProfile?.display_name}</p>
            {isNip05Verified && <Icon name="checkmark-c1" isColored />}
          </div>
          {!isLNBC && (
            <p className="p-one-line gray-c">
              {userProfile.lud16
                ? userProfile.lud16.length < 40
                  ? userProfile.lud16
                  : shortenKey(userProfile.lud16, 10)
                : shortenKey(recipientAddr, 10)}
            </p>
          )}
          {isLNBC && (
            <p className="p-one-line gray-c p-italic">{t("ANOiCGe")}</p>
          )}
        </div>
      )}
    </div>
  );
};

const checkAlbyToken = async (wallets, activeWallet) => {
  let tokenExpiry = activeWallet.data.created_at + activeWallet.data.expires_in;
  let currentTime = Math.floor(Date.now() / 1000);
  if (tokenExpiry > currentTime)
    return {
      wallets,
      activeWallet,
    };
  try {
    let fd = new FormData();
    fd.append("refresh_token", activeWallet.data.refresh_token);
    fd.append("grant_type", "refresh_token");
    const access_token = await axios.post(
      "https://api.getalby.com/oauth/token",
      fd,
      {
        auth: {
          username: process.env.NEXT_PUBLIC_ALBY_CLIENT_ID,
          password: process.env.NEXT_PUBLIC_ALBY_SECRET_ID,
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    let tempWallet = { ...activeWallet };
    tempWallet.data = {
      ...access_token.data,
      created_at: Math.floor(Date.now() / 1000),
    };
    let tempWallets = Array.from(wallets);
    let index = wallets.findIndex((item) => item.id === activeWallet.id);
    tempWallets[index] = tempWallet;
    updateWallets(tempWallets);
    return {
      wallets: tempWallets,
      activeWallet: tempWallet,
    };
  } catch (err) {
    console.log(err);
    return {
      wallets,
      activeWallet,
    };
  }
};
